import { useEffect, useRef, useState } from 'react'
import { SRGBColorSpace, TextureLoader, VideoTexture, type Texture, type Mesh } from 'three'
import { config } from '../config'

/**
 * 动态视频背景（优先）→ 静态图片回退 → 纯色兜底。
 *
 * 视频要求：
 *   - 路径：/assets/backgrounds/scene-main.mp4（或 .webm）
 *   - 推荐编码：H.264 + AAC（mp4），无需音轨，建议静音
 *   - 浏览器要求 muted + playsInline 才允许 autoplay
 */
export function Background() {
  const [tex, setTex] = useState<Texture | null>(null)
  const [failed, setFailed] = useState(false)
  const meshRef = useRef<Mesh>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    let cancelled = false

    const videoPath = config.assets.paths.backgroundVideo

    // 先 HEAD 检查视频是否存在
    fetch(videoPath, { method: 'HEAD' })
      .then((res) => {
        if (!res.ok) throw new Error('no video')
        if (cancelled) return

        const video = document.createElement('video')
        video.src = videoPath
        video.autoplay = true
        video.loop = true
        video.muted = true
        video.playsInline = true
        video.style.display = 'none'
        document.body.appendChild(video)
        videoRef.current = video

        video.addEventListener('canplaythrough', () => {
          if (cancelled) return
          video.play().catch(() => {/* autoplay policy: still ok, VideoTexture waits for frames */})
          const vTex = new VideoTexture(video)
          vTex.colorSpace = SRGBColorSpace
          setTex(vTex)
        }, { once: true })

        video.addEventListener('error', () => {
          if (!cancelled) fallbackToImage()
        }, { once: true })

        // 触发加载
        video.load()
      })
      .catch(() => {
        if (!cancelled) fallbackToImage()
      })

    function fallbackToImage() {
      const loader = new TextureLoader()
      loader.load(
        config.assets.paths.background,
        (t) => {
          if (cancelled) return
          t.colorSpace = SRGBColorSpace
          t.anisotropy = 4
          setTex(t)
        },
        undefined,
        () => { if (!cancelled) setFailed(true) },
      )
    }

    return () => {
      cancelled = true
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.remove()
        videoRef.current = null
      }
    }
  }, [])

  return (
    <mesh
      ref={meshRef}
      position={[0, 0, -20]}
      renderOrder={-10}
      onUpdate={(self) => {
        const apply = (mat: any) => {
          mat.depthTest = false
          mat.depthWrite = true
          mat.needsUpdate = true
        }
        const m = self.material
        if (Array.isArray(m)) m.forEach(apply)
        else apply(m)
      }}
    >
      <planeGeometry args={[60, 36]} />
      {failed || !tex ? (
        <meshBasicMaterial color={config.scene.backgroundColor} />
      ) : (
        <meshBasicMaterial map={tex} toneMapped={false} />
      )}
    </mesh>
  )
}
